import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { memo, useCallback, useEffect, useRef } from 'react';
import { Pagination, styled } from '@mui/material';
import { PaginatorItem } from './PaginatorItem';
import { RowsPerPage } from './RowsPerPage';
import { DisplayedRows } from './DisplayedRows';
import { isMobileApp } from 'utils/common';
import { DEFAULT_PAGE_SIZE } from 'utils/hooks/useTableQueryControls';
import { useDispatch } from 'react-redux';
import { uiStateActions } from 'store/entities/ui';

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'noWrap',
})<{ noWrap?: boolean }>(({ theme, noWrap = false }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: noWrap ? 'nowrap' : 'wrap',
  gap: '20px',
  marginTop: '10px',
  [theme.breakpoints.down('sm')]: {
    gap: '5px',
  },
}));

interface Props {
  paginator: {
    pageSize: number;
    pageIndex: number;
  };
  setPaginator: Dispatch<
    SetStateAction<{
      pageSize: number;
      pageIndex: number;
    }>
  >;
  rowsCount?: number;
  showAllRows?: boolean;
  zIndex?: number;
  disablePageSizeChange?: boolean;
  onPageIndexChange?: (pageIndex: number) => void;
  registerComponentHeight?: boolean;
  noWrap?: boolean;
  dataTestIdPrefix?: string;
  testIdPrefix?: string;
  compact?: boolean;
}

export const Paginator = memo(
  ({
    paginator,
    setPaginator,
    rowsCount = 0,
    showAllRows = false,
    zIndex,
    disablePageSizeChange,
    onPageIndexChange,
    registerComponentHeight = true,
    noWrap = false,
    dataTestIdPrefix,
    testIdPrefix,
    compact = false,
  }: Props) => {
    const resolvedTestIdPrefix = dataTestIdPrefix ?? testIdPrefix;
    const { pageSize, pageIndex } = paginator;
    const mobileApp = isMobileApp();
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const handleChangePage = useCallback(
      (_: ChangeEvent<unknown>, newPage: number) => {
        const nextPageIndex = newPage - 1;
        setPaginator((prev) => ({
          ...prev,
          pageIndex: nextPageIndex,
        }));
        onPageIndexChange?.(nextPageIndex);
      },
      [setPaginator, onPageIndexChange]
    );

    const totalPages = Math.ceil(rowsCount / (pageSize || DEFAULT_PAGE_SIZE));

    useEffect(() => {
      if (!registerComponentHeight) return;

      if (ref.current) {
        const height = ref.current.getBoundingClientRect().height;
        dispatch(uiStateActions.setComponentHeight({ id: 'table-paginator', height }));
      }

      return () => {
        dispatch(uiStateActions.removeComponentHeight('table-paginator'));
      };
    }, [dispatch, registerComponentHeight]);

    return (
      <Wrapper ref={ref} noWrap={noWrap}>
        <RowsPerPage
          rowsPerPage={pageSize}
          showAll={showAllRows}
          setRowsPerPage={setPaginator}
          zIndex={zIndex}
          disabled={disablePageSizeChange}
          testIdPrefix={resolvedTestIdPrefix}
        />
        <DisplayedRows rowsCount={rowsCount} page={pageIndex + 1} rowsPerPage={pageSize} testIdPrefix={resolvedTestIdPrefix} />
        <Pagination
          siblingCount={compact || mobileApp ? 0 : 1}
          count={totalPages}
          size="small"
          page={pageIndex + 1}
          onChange={handleChangePage}
          sx={
            noWrap
              ? {
                  flexShrink: 0,
                  '& .MuiPagination-ul': {
                    flexWrap: 'nowrap',
                  },
                }
              : undefined
          }
          renderItem={(item) => (
            <PaginatorItem
              {...item}
              hidePrevButton={pageIndex === 0}
              hideNextButton={pageIndex >= totalPages - 1}
              testIdPrefix={resolvedTestIdPrefix}
            />
          )}
        />
      </Wrapper>
    );
  }
);
